<template>
  <content-subpage
    class="add-to-notebook-subpage"
    :visible="visible"
    :has-parent-subpage="props.params.hasParentSubpage"
    :titles="titles"
    :back-label="backLabel"
    :show-content-title="false"
    @content-subpage-hidden="actionHidden"
    @content-subpage-back="actionBack"
  >
    <template #main-content>
      <div class="notebook-listing">
        <span class="label">{{ t("notebook_added_to_label") }}</span>
        <div>
          <v-data-table
            v-model:page="options.page"
            v-model:sort-by="options.sortBy"
            v-model:sort-desc="options.sortDesc"
            class="notebook-listing-table"
            :headers="headers"
            :items="sortedAnnotationLinkedNotebooks"
            :items-per-page="options.itemsPerPage"
            :loading="isLoading"
          >
            <template #no-data>
              <empty-placeholder>
                <template #icon>
                  <NotebookEmpty />
                </template>
                {{ t("notebook_table_no_notebooks_available_label") }}
              </empty-placeholder>
            </template>
            <template #[`item.notebookTitle`]="{ item }">
              <data-table-cell class="info">
                <ellipsis-text :text="getStringProp(item, 'notebookTitle')" />
              </data-table-cell>
            </template>
            <template #[`item.pageTitle`]="{ item }">
              <data-table-cell class="page-title">
                <ellipsis-text :text="getStringProp(item, 'pageTitle')" />
              </data-table-cell>
            </template>
            <template #[`item.action`]="{ item }">
              <data-table-cell class="action">
                <cv-button
                  kind="ghost"
                  size="small"
                  class="icon-button external-link"
                  @click.stop="handleOpenLink(item)"
                >
                  <svg-icon name="ExternalLink24" kind="action" />
                </cv-button>
              </data-table-cell>
            </template>
          </v-data-table>
        </div>
      </div>
    </template>
    <template #sub-content>
      <div class="content">
        <span class="title">{{ t("notebook_add_to_notebook_label") }}</span>
        <div class="dropdown-group notebook">
          <div class="notebook-tip">
            <span class="label">
              {{ t("notebook_label") }}
            </span>
            <cv-interactive-tooltip direction="bottom">
              <template #trigger>
                <svg-icon name="Info24" kind="action" />
              </template>
              <template #content>
                <p v-safe-html="notebookTooltipLabel" />
              </template>
            </cv-interactive-tooltip>
          </div>
          <dropdown-component :value="notebookSelected" @change="handleNotebookDropdownChange">
            <template v-if="selectedNotebookTitle" #internal-caption>
              <svg-icon name="Notebook32" kind="indicative" />
              <ellipsis-text :text="selectedNotebookTitle" />
            </template>
            <cv-dropdown-item
              v-for="notebook in mostRecentNotebooks"
              :key="notebook.id"
              :value="notebook.id.toString()"
            >
              <div class="icon-label-group">
                <svg-icon name="Notebook32" kind="indicative" />
              </div>
              <span>{{ notebook.title }}</span>
            </cv-dropdown-item>
          </dropdown-component>
        </div>
        <div class="dropdown-group page">
          <span class="label">
            {{ t("notebook_page_label") }}
          </span>
          <dropdown-component
            :value="pageSelected"
            :disabled="!notebookSelected"
            :class="{ 'dropdown-error': showErrorMessage }"
            @change="handlePageDropdownChange"
          >
            <template v-if="selectedPageIndex !== -1" #internal-caption>
              <span v-if="pageSelected !== 'newPage'" class="icon-pagination">
                <svg-icon name="Pagination32" kind="indicative" />
                <span class="page-label"> {{ selectedPageIndex }} </span>
              </span>
              <ellipsis-text :text="selectedPageTitle" />
            </template>
            <cv-dropdown-item
              v-for="(page, index) in pageOptions"
              :key="page.id"
              :value="page.id.toString()"
            >
              <div v-if="page.id !== 'newPage'" class="icon-label-group">
                <svg-icon name="Pagination32" kind="indicative" />
                <span class="page-label"> {{ index }} </span>
              </div>
              <span class="page-title">{{ page.title }}</span>
            </cv-dropdown-item>
          </dropdown-component>
          <span v-if="showErrorMessage" class="error-message">
            <svg-icon name="Error16" kind="indicative" />
            {{ errorMessage }}
          </span>
        </div>
      </div>
    </template>
    <template #action-button>
      <fetch-data-button
        class="btn-create"
        tip-position="top"
        tip-alignment="end"
        :icon-only="false"
        :disabled="isAddButtonDisabled"
        :loading="isAddButtonLoading"
        :error="showErrorMessage"
        :error-message="addButton.errorMessage"
        @click="handleAddButton"
        @reset-error-state="handleResetErrorState"
      >
        <span v-if="showErrorMessage">{{ t("button_state_error") }}</span>
        <template v-else>
          <svg-icon name="NotebookAdd32" kind="action" />
          <span>{{ t("button_add") }}</span>
        </template>
      </fetch-data-button>
    </template>
  </content-subpage>
</template>
<script setup lang="ts">
import notebookApi from "@modules/notebook-editor/notebook-editor-api.ts";
import { useErrorHandler } from "@plugins/common/composables/error.ts";
import { useContentSubpage } from "@plugins/content-subpage/composables/content-subpage.ts";
import { isEmpty } from "@utils/var.check.ts";
import { computed, onBeforeMount, ref } from "vue";
import { useI18n } from "vue-i18n";
import {
  NOTEBOOK_LISTING_URL,
  NOTEBOOK_MAX_NOTE,
  NOTEBOOK_MAX_PAGE,
  NOTEBOOK_MAX_RECENT,
  NOTEBOOK_CREATE_NEW_NOTEBOOK_ID,
  NOTEBOOK_CREATE_NEW_NOTEBOOK_UUID,
} from "@modules/notebook-editor/notebook-editor-helper.ts";
import {
  AnnotationLinkedNotebookInfo,
  DataTableOptions,
  Notebook,
  NotebookRecentModel,
} from "@modules/notebook-editor/notebook-editor-types.ts";
import { useToast } from "@plugins/toast/composables/toast-instance.ts";
import { useRouter } from "vue-router";
import notebookPageNoteApi from "@apis/notebook-page-note.api.ts";
import notebookPageApi from "@apis/notebook-page-api.ts";
import NotebookEmpty from "@images/placeholder/empty-states/NotebookEmpty.svg";
import DeviceUtils from "@utils/device.utils.ts";
import { useModal } from "@plugins/modal/composables/modal-instance.ts";
import NotebookCreateNewNotebookModal from "@modules/notebook-editor/components/NotebookCreateNewNotebookModal.vue";
import DropdownComponent from "@plugins/common/components/DropdownComponent.vue";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";
import EllipsisText from "@plugins/common/components/EllipsisText.vue";
import DataTableCell from "@plugins/vuetify/components/DataTableCell.vue";
import EmptyPlaceholder from "@plugins/common/components/EmptyPlaceholder.vue";
import FetchDataButton from "@plugins/common/components/FetchDataButton.vue";
import ContentSubpage from "@plugins/content-subpage/components/ContentSubpage.vue";

interface ContentSubpageParams {
  id: string;
  hasParentSubpage?: boolean;
  [key: string]: unknown;
}

const props = defineProps<{ params: ContentSubpageParams }>();

const {
  visible,
  titles,
  actionBack,
  actionHidden,
  actionBackCallBack,
  actionHiddenCallBack,
  setVisible,
  isComponentDirty,
  promptComponentDirty,
  removeComponentDirty,
  setDirty,
  removeDirty,
} = useContentSubpage(props.params);
const { t } = useI18n();
const errorHandler = useErrorHandler();
const toast = useToast();
const router = useRouter();
const modal = useModal();

const parentSubpageName = ref("");
const headers = ref([
  {
    title: t("notebook_table_notebook_title_label"),
    class: "notebook-title",
    key: "notebookTitle",
    sortable: true,
  },
  {
    title: t("notebook_table_page_title_label"),
    class: "page-title",
    key: "pageTitle",
    sortable: false,
  },
  { title: "", class: "action", key: "action", sortable: false },
]);
const mostRecentNotebooks = ref<NotebookRecentModel[]>([]);
const notebookSelected = ref<string | null>(null);
const pageSelected = ref<string | null>(null);
const annotationLinkedNotebooks = ref<AnnotationLinkedNotebookInfo[]>([]);
const itemsPerPage = ref(DeviceUtils.isMobile() ? 10 : 20);
const options = ref<DataTableOptions>({
  page: 1,
  itemsPerPage: itemsPerPage.value,
  sortBy: [{ key: "notebookTitle", order: "asc" }],
  sortDesc: [false],
});
const totalItems = ref(0);
const isLoading = ref(false);

const addButton = ref({ error: false, errorMessage: "", disabled: false });
const isAddButtonLoading = ref(false);
const showErrorMessage = ref(false);

const backLabel = computed(() => {
  return `${t("content_subpage_go_back_link")} ${parentSubpageName.value}`;
});
const pages = computed(() => {
  const nb = mostRecentNotebooks.value.find((n) => n.id.toString() === notebookSelected.value);
  return nb?.pages ?? [];
});
const pageOptions = computed(() => [
  { id: "newPage", title: t("notebook_add_to_new_page_label") },
  ...pages.value.map((p) => ({ id: p.id.toString(), title: p.title })),
]);
const isPageOverNoteLimit = computed(() =>
  pages.value.some(
    (p) => pageSelected.value === p.id.toString() && p.noteCount >= NOTEBOOK_MAX_NOTE,
  ),
);
const isNotebookOverPageLimit = computed(() => pages.value.length >= NOTEBOOK_MAX_PAGE);
const errorMessage = computed(() => {
  return isPageOverNoteLimit.value
    ? t("notebook_max_notes_error_label", { noteLimit: NOTEBOOK_MAX_NOTE })
    : t("notebook_max_pages_error_label", { pageLimit: NOTEBOOK_MAX_PAGE });
});
const contentSubpageId = computed(() => props.params.id);
const isAddButtonDisabled = computed(() => !pageSelected.value);
const notebookTooltipLabel = computed(() =>
  t("notebook_most_recent_tooltip_label", {
    recentNotebookLimit: NOTEBOOK_MAX_RECENT,
    notebookListingPageUrl: NOTEBOOK_LISTING_URL,
  }),
);
const selectedNotebookTitle = computed(
  () => mostRecentNotebooks.value.find((n) => n.id.toString() === notebookSelected.value)?.title,
);
const selectNotebookUuid = computed(
  () => mostRecentNotebooks.value.find((n) => n.id.toString() === notebookSelected.value)?.uuid,
);
const selectedPageIndex = computed(() =>
  pageOptions.value.findIndex((p) => p.id === pageSelected.value),
);
const selectedPageTitle = computed(() => {
  const page = pageOptions.value.find((p) => p.id === pageSelected.value);
  return page ? page.title : null;
});
const sortedAnnotationLinkedNotebooks = computed(() => {
  const list = [...annotationLinkedNotebooks.value];
  const primary = options.value.sortBy[0];
  if (!primary) {
    return list;
  }

  const secondaryKey = primary.key === "notebookTitle" ? "pageTitle" : "notebookTitle";
  const dir = primary.order === "asc" ? 1 : -1;

  list.sort((a, b) => {
    const cmpPrimary = a[primary.key].localeCompare(b[primary.key]) * dir;
    if (cmpPrimary !== 0) return cmpPrimary;

    return a[secondaryKey].localeCompare(b[secondaryKey]) * dir;
  });

  return list;
});

const getStringProp = (item: Record<string, unknown> | null | undefined, prop: string): string => {
  if (isEmpty(item)) return "";
  const record = item as Record<string, unknown>;
  const v = record[prop];
  return typeof v === "string" ? v : "";
};
const handleNotebookDropdownChange = (newId: string) => {
  handleResetErrorState();
  notebookSelected.value = newId;
  if (newId === NOTEBOOK_CREATE_NEW_NOTEBOOK_ID.toString()) {
    modal.show(
      NotebookCreateNewNotebookModal,
      {
        title: t("notebook_create_new_notebook_label"),
      },
      {
        createdNotebook: handleCreatedNewNotebook,
        cancel: handleCancelCreateNewNotebook,
      },
    );
  }
  pageSelected.value = null;
  setDirty(contentSubpageId.value);
};
const handleCreatedNewNotebook = (notebook: Notebook) => {
  mostRecentNotebooks.value = mostRecentNotebooks.value.toSpliced(1, 0, {
    id: notebook.id,
    uuid: notebook.uuid,
    title: notebook.title,
    pages: [],
  });
  notebookSelected.value = notebook.id.toString();
  toast.show("success", {
    title: t("notebook_create_new_notebook_success_message", { notebookTitle: notebook.title }),
  });
};
const handleCancelCreateNewNotebook = () => {
  notebookSelected.value = null;
  pageSelected.value = null;
};
const handlePageDropdownChange = (newId: string) => {
  handleResetErrorState();
  pageSelected.value = newId;
  setDirty(contentSubpageId.value);
};
const handleAddButton = async () => {
  if (
    isPageOverNoteLimit.value ||
    (isNotebookOverPageLimit.value && pageSelected.value === "newPage")
  ) {
    showErrorMessage.value = true;
    return;
  }

  try {
    let selectedPageId = Number(pageSelected.value);

    if (pageSelected.value === "newPage") {
      const newPage = await createNewPage();
      selectedPageId = newPage.id;
    }

    const response = await notebookPageNoteApi.createNoteAnnotation({
      pageId: selectedPageId,
      annotationId: Number(props.params.annotationId),
    });
    if (response.code !== "ok") {
      const error = Object.values(response.errorMap)[0] ?? "";
      throw new Error(error);
    }

    const routeData = router.resolve({
      name: "NotebookEditorPage",
      params: { notebookUuid: selectNotebookUuid.value, pageId: selectedPageId },
    });

    toast.show("success", {
      title: t("notebook_add_to_notebook_success_label", {
        notebookTitle: selectedNotebookTitle.value,
      }),
      caption: t("notebook_add_to_notebook_success_view_label", { pageUrl: routeData.href }),
      sanitizeCaption: false,
      cssClass: "notebook-toast",
    });
  } catch {
    errorHandler.passiveApiFailHandler();
  } finally {
    removeDirty(contentSubpageId.value);
    actionBack();
  }
};

const createNewPage = async () => {
  try {
    const pageCount = pages.value.length;
    const response = await notebookPageApi.createNotebookPage({
      title: "Untitled",
      notebookId: Number(notebookSelected.value),
      targetPageIndex: pageCount + 1,
    });

    if (response.code !== "ok") {
      const error = Object.values(response.errorMap)[0] ?? "";
      throw new Error(error);
    }
    return response.data;
  } catch (error) {
    throw error;
  }
};

const handleOpenLink = (notebook) => {
  const routeData = router.resolve({
    name: "NotebookEditorPage",
    params: { notebookUuid: notebook.notebookUuid, pageId: notebook.pageId },
  });
  window.open(routeData.href, "_blank");
};

const fetchAnnotationLinkedNotebooks = async (annotationId: number) => {
  try {
    isLoading.value = true;
    const response = await notebookApi.fetchAnnotationLinkedNotebooks(annotationId);
    if (response.code !== "ok") {
      const error = Object.values(response.errorMap)[0] ?? "";
      throw new Error(error);
    }
    annotationLinkedNotebooks.value = response.data;
    totalItems.value = response.data.length;
  } catch {
    errorHandler.passiveApiFailHandler();
  } finally {
    isLoading.value = false;
  }
};

const handleResetErrorState = () => {
  addButton.value.error = false;
  addButton.value.errorMessage = "";
  showErrorMessage.value = false;
};

onBeforeMount(async () => {
  if (props.params.hasParentSubpage) {
    const len = titles.value.length;
    parentSubpageName.value = titles.value[len - 1] || "";
  }
  titles.value.push(t("notebook_add_to_notebook_label"));
  fetchAnnotationLinkedNotebooks(Number(props.params.annotationId));
  try {
    const response = await notebookApi.fetchMostRecentNotebooks();
    if (response.code !== "ok") {
      const error = Object.values(response.errorMap)[0] ?? "";
      throw new Error(error);
    }
    mostRecentNotebooks.value = response.data;
    mostRecentNotebooks.value.unshift({
      id: NOTEBOOK_CREATE_NEW_NOTEBOOK_ID,
      uuid: NOTEBOOK_CREATE_NEW_NOTEBOOK_UUID,
      title: t("notebook_create_new_notebook_label"),
      pages: null,
    });
  } catch {
    errorHandler.passiveApiFailHandler();
  }
});

defineExpose({
  params: props.params,
  actionHiddenCallBack: actionHiddenCallBack,
  actionBackCallBack: actionBackCallBack,
  setVisible: setVisible,
  isComponentDirty: isComponentDirty,
  removeComponentDirty: removeComponentDirty,
  promptComponentDirty: promptComponentDirty,
});
</script>
<style lang="scss">
@use "@styles/abstracts/mixins" as *;
@use "@styles/themes/sls-theme/tokens/colors" as *;
@use "@styles/themes/sls-theme/tokens/effects" as *;
@use "@styles/themes/sls-theme/tokens/spacing" as *;
@use "@styles/themes/sls-theme/tokens/typo" as *;

.add-to-notebook-subpage {
  .notebook-listing {
    > .label {
      @include carbon--type-style("productive-heading-01", true);
      display: block;
      margin-bottom: $spacing-05;
    }

    .notebook-listing-table {
      .v-table__wrapper {
        border-radius: 10px;
      }
      table {
        tbody {
          tr.v-data-table-rows-no-data {
            > td::before {
              content: none;
            }
          }
        }
      }
    }
  }

  .error-message {
    @include carbon--type-style("helper-text-01");
    display: flex;
    align-items: center;
    margin-top: $spacing-02;
    gap: $spacing-03;
    color: $support-01;
    .sls-icon {
      min-width: 16px;
    }
  }

  .content {
    > .title {
      @include carbon--type-style("productive-heading-02");
      display: block;
      margin-bottom: $spacing-05;
    }

    .notebook-tip {
      display: flex;
      align-items: center;
      margin-bottom: $spacing-02;

      .bx--tooltip__trigger {
        &:hover {
          .sls-icon {
            fill: $ui-01;
          }
        }
      }

      .bx--tooltip__trigger {
        &:hover,
        &:focus {
          .sls-icon {
            fill: none;
          }
        }
      }

      .cv-interactive-tooltip {
        max-height: 24px;
        .sls-icon {
          [stroke] {
            stroke: $interactive-01;
          }

          [fill] {
            fill: $interactive-01;
          }
        }
      }
    }

    .dropdown-group {
      margin-bottom: $spacing-05;

      &.notebook {
        .bx--list-box__label {
          gap: $spacing-02;
          .ellipsis-text {
            @include carbon--type-style("productive-heading-01", true);
            color: $text-02;
          }
          .sls-icon {
            min-width: 32px;
          }
        }

        .bx--dropdown-item {
          .bx--dropdown-link {
            > span {
              @include carbon--type-style("productive-heading-01", true);
              color: $text-02;
              margin-left: $spacing-02;
            }
          }
        }
      }

      &.page {
        .bx--list-box__label {
          @include line-clamp(1);
          display: flex;
          align-items: center;
          gap: $spacing-03;
          .ellipsis-text {
            @include carbon--type-style("productive-heading-01", true);
            color: $text-02;
          }
        }
        span.icon-pagination {
          position: relative;
          max-height: 32px;
          .page-label {
            @include carbon--type-style("card-label-01");
            position: absolute;
            left: 0.8rem;
            top: 0.6rem;
            color: $interactive-01;
          }
        }
      }
      .label {
        @include carbon--type-style("body-short-01");
        color: $text-02;
      }

      > .dropdown-component {
        margin-top: $spacing-02;
      }

      .bx--dropdown-item {
        .page-title {
          @include carbon--type-style("productive-heading-01", true);
          @include line-clamp(1);
          margin-left: $spacing-03;
        }
      }
    }
    .icon-label-group {
      position: relative;
      height: 32px;
      width: 32px;

      .page-label {
        @include carbon--type-style("card-label-01");
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        color: $interactive-01;
      }
    }

    .dropdown-error {
      .bx--list-box {
        border: 1px solid $support-01;

        &:focus,
        &:hover {
          border: 1px solid $support-01;
        }
      }

      .bx--list-box--expanded {
        border-color: $support-01;
      }
    }
  }
}
</style>
